import { useCallback, useEffect, useState } from 'react';
import { QuoteAnalyticsSummary, QuoteTimeSeriesPoint } from '../types';
import { useToast } from '../contexts/ToastContext';
import {
  QuoteAnalyticsDomain,
  fetchQuoteSummary,
  fetchQuoteDaily,
  fetchQuoteWeekly,
  fetchQuoteMonthly
} from '../services/quote-analytics.service';

export interface QuoteAnalyticsDateRange {
  startDate: string;
  endDate: string;
}

interface UseQuoteAnalyticsReturn {
  summary: QuoteAnalyticsSummary | null;
  dailyData: QuoteTimeSeriesPoint[];
  weeklyData: QuoteTimeSeriesPoint[];
  monthlyData: QuoteTimeSeriesPoint[];
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useQuoteAnalytics(
  domain: QuoteAnalyticsDomain,
  accessToken: string,
  dateRange: QuoteAnalyticsDateRange
): UseQuoteAnalyticsReturn {
  const [summary, setSummary] = useState<QuoteAnalyticsSummary | null>(null);
  const [dailyData, setDailyData] = useState<QuoteTimeSeriesPoint[]>([]);
  const [weeklyData, setWeeklyData] = useState<QuoteTimeSeriesPoint[]>([]);
  const [monthlyData, setMonthlyData] = useState<QuoteTimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const refresh = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const [summaryResult, dailyResult, weeklyResult, monthlyResult] = await Promise.all([
        fetchQuoteSummary(domain, dateRange.startDate, dateRange.endDate, accessToken),
        fetchQuoteDaily(domain, dateRange.startDate, dateRange.endDate, accessToken),
        fetchQuoteWeekly(domain, dateRange.startDate, dateRange.endDate, accessToken),
        fetchQuoteMonthly(domain, dateRange.startDate, dateRange.endDate, accessToken)
      ]);

      setSummary(summaryResult);
      setDailyData(Array.isArray(dailyResult) ? dailyResult : []);
      setWeeklyData(Array.isArray(weeklyResult) ? weeklyResult : []);
      setMonthlyData(Array.isArray(monthlyResult) ? monthlyResult : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar relatórios';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [domain, accessToken, dateRange.startDate, dateRange.endDate, toast]);

  useEffect(() => {
    if (accessToken) {
      refresh();
    }
  }, [accessToken, domain, dateRange.startDate, dateRange.endDate]);

  return { summary, dailyData, weeklyData, monthlyData, loading, refresh };
}
